// ============================================================
// MODERN MERCENARIES — Core Types
// ============================================================

export type MissionStatus = 'open' | 'active' | 'started' | 'done' | 'cancelled';
export type HireStatus = 'pending' | 'hired' | 'declined';
export type UserRole = 'user' | 'admin';
export type Lang = 'en' | 'fr' | 'es';
export type Theme = 'light' | 'dark';

export interface Skill {
  name: string;
  level: number; // 1–10
}

export interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  country: string;
  city: string;
  bio: string;
  avatar: string | null;
  skills: Skill[];
  rating: number;
  ratingCount: number;
  reports: string[]; // array of reporter user IDs
  blocked: boolean;
  role: UserRole;
  createdAt: number;
  updatedAt: number;
}

export interface PaymentBreakdown {
  hired: number;    // 10% on hire
  started: number;  // 40% on start
  finished: number; // 50% on finish
}

export interface Mission {
  id: string;
  title: string;
  description: string;
  reward: number;           // in EUR
  estimatedHours: number;
  category: string;
  location: string;
  skills: string[];
  posterId: string;
  status: MissionStatus;
  applicantIds: string[];
  hiredId: string | null;
  payments: PaymentBreakdown;
  stripePaymentIntentId: string | null;
  createdAt: number;
  updatedAt: number;
}

export interface Message {
  id: string;
  chatId: string;
  senderId: string | 'system';
  text: string;
  ts: number;
  read: boolean;
}

export interface Chat {
  id: string;
  missionId: string | null;
  participantIds: string[];   // [posterId, applicantId] or [userId1, userId2]
  hireStatus: HireStatus | 'n/a';
  missionStatus: MissionStatus | 'n/a';
  messages: Message[];
  isDirect: boolean;
  lastMessageAt: number;
}

export interface Report {
  id: string;
  reporterId: string;
  targetId: string;
  reason: string;
  details: string;
  ts: number;
}

export interface AuthState {
  currentUser: User | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => void;
  updateUser: (data: Partial<User>) => void;
}

export interface RegisterData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  country: string;
  city: string;
}

export interface AppState {
  theme: Theme;
  lang: Lang;
  toggleTheme: () => void;
  setLang: (lang: Lang) => void;
}

export interface ToastType {
  id: string;
  message: string;
  type: 'success' | 'error' | 'info';
}

export const MISSION_CATEGORIES = [
  'Errands',
  'Tech',
  'Creative',
  'Research',
  'Transport',
  'Social',
  'Writing',
  'Design',
  'Photography',
  'Other',
] as const;

export type MissionCategory = typeof MISSION_CATEGORIES[number];

export const COUNTRIES = [
  'France', 'Spain', 'Germany', 'UK', 'USA',
  'Canada', 'Italy', 'Portugal', 'Belgium', 'Netherlands', 'Other',
] as const;

export const PLATFORM_FEE_PERCENT = 0.07; // 7%

// Payment milestones
export const PAYMENT_ON_HIRE = 0.10;    // 10%
export const PAYMENT_ON_START = 0.40;   // 40%
export const PAYMENT_ON_FINISH = 0.50;  // 50%
